import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBookings } from '../hooks/useBookings';
import Topbar from '../components/layout/Topbar';
import { BookingList } from '../components/bookings/BookingItem';
import { Button, Panel, PanelBody, Spinner } from '../components/ui';
import { createBooking } from '../services/firebase';
import { useWindowSize } from '../hooks/useWindowSize';

const STATUS_FILTERS = [
  { id: 'Tutte', label: 'Tutte' },
  { id: 'confirmed', label: 'Confermate' },
  { id: 'pending', label: 'In attesa' },
  { id: 'blocked', label: 'Bloccate' },
  { id: 'cancelled', label: 'Cancellate' },
];

const DATE_TYPES = [
  { id: 'single', label: 'Giorno singolo', icon: '📅' },
  { id: 'range', label: 'Periodo continuativo', icon: '📆' },
  { id: 'multiple', label: 'Date non consecutive', icon: '🗓️' },
  { id: 'seasonal', label: 'Stagionale (giorni sett.)', icon: '🌊' },
];

const WEEKDAYS_FULL = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

export default function Bookings() {
  const { admin, beach } = useAuth();
  const { orders, loading, cancel, confirm, refetch } = useBookings(admin?.beachCode);
  const { isMobile } = useWindowSize();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tutte');
  const [serviceFilter, setServiceFilter] = useState('Tutti');
  const [dateFilter, setDateFilter] = useState('future');
  const [showNewForm, setShowNewForm] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // Filtri servizio dinamici dai servizi del lido
  const serviceFilters = useMemo(() => [
    { id: 'Tutti', label: 'Tutti' },
    ...(beach?.servizi || []).map(s => ({ id: s.id, label: s.label, icona: s.icona })),
  ], [beach?.servizi]);

  const filtered = useMemo(() => {
    const nowStr = today;
    const weekStr = new Date(new Date().getTime() + 7 * 86400000).toISOString().split('T')[0];
    const monthStr = new Date(new Date().getTime() + 30 * 86400000).toISOString().split('T')[0];

    return orders.filter(o => {
      const primaData = o.selectedDate?.[0] || '';
      const matchDate = (() => {
        if (dateFilter === 'today') return primaData === nowStr;
        if (dateFilter === 'week') return primaData >= nowStr && primaData <= weekStr;
        if (dateFilter === 'month') return primaData >= nowStr && primaData <= monthStr;
        if (dateFilter === 'future') return primaData >= nowStr;
        if (dateFilter === 'past') return primaData < nowStr;
        return true;
      })();

      // Ricerca per nome, telefono, ID ordine, ID cella
      const matchSearch = !search || [
        o.guestName, o.guestPhone, o.userId, o.id,
        ...(o.celle?.map(c => c.id) || []),
      ].some(v => v?.toLowerCase().includes(search.toLowerCase()));

      // Filtro status
      const matchStatus =
        statusFilter === 'Tutte' || o.status === statusFilter;

      // Filtro servizio — compatibile con entrambe le strutture
      const matchService =
        serviceFilter === 'Tutti' ||
        o.celle?.some(c => c.type === serviceFilter) ||
        (o.serviceSelected && serviceFilter === o.serviceSelected);

      return matchDate && matchSearch && matchStatus && matchService;
    });
  }, [orders, search, statusFilter, serviceFilter, dateFilter]);

  // Reset pagina quando cambiano i filtri
  React.useEffect(() => { setPage(1); }, [search, statusFilter, serviceFilter, dateFilter]);

  // Paginazione
  const pagedGrouped = useMemo(() => {
    const sliced = filtered.slice(0, page * PAGE_SIZE);
    const map = {};
    sliced.forEach(o => {
      const date = o.selectedDate?.[0] || 'Senza data';
      if (!map[date]) map[date] = [];
      map[date].push(o);
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a));
  }, [filtered, page]);

  const hasMore = filtered.length > page * PAGE_SIZE;

  // Raggruppamento per data
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(o => {
      const date = o.selectedDate?.[0] || 'Senza data';
      if (!map[date]) map[date] = [];
      map[date].push(o);
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a));
  }, [filtered]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        title="Prenotazioni"
        actions={
          <Button variant="primary" onClick={() => setShowNewForm(!showNewForm)}>
            + Nuova prenotazione
          </Button>
        }
      />

      <div style={{ padding: isMobile ? '12px 16px' : '24px 28px', overflowY: 'auto', flex: 1 }}>
        {showNewForm && (
          <NewBookingForm
            beachCode={admin?.beachCode}
            servizi={beach?.servizi || []}
            onSave={async (data) => {
              await createBooking(admin.beachCode, data);
              setShowNewForm(false);
              refetch();
            }}
            onCancel={() => setShowNewForm(false)}
          />
        )}

        <Panel>
          <PanelBody>
            {/* Barra ricerca */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '0 12px', marginBottom: '14px',
            }}>
              <SearchIcon />
              <input
                type="text"
                placeholder="Cerca per nome, telefono, ID posto..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  background: 'transparent', border: 'none', color: '#fff',
                  fontSize: '13px', padding: '9px 0', flex: 1, outline: 'none',
                  fontFamily: 'var(--font-sans)',
                }}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px' }}>×</button>
              )}
            </div>

            {/* Filtri status */}
            <div style={{
              display: 'flex', gap: '6px', marginBottom: '8px',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              overflowX: isMobile ? 'auto' : 'unset',
              paddingBottom: isMobile ? '4px' : 0,
              WebkitOverflowScrolling: 'touch',
            }}>
              {STATUS_FILTERS.map(f => (
                <Chip key={f.id} label={f.label} active={statusFilter === f.id} onClick={() => setStatusFilter(f.id)} />
              ))}
            </div>

            {/* Filtri servizio — dinamici */}
            <div style={{
              display: 'flex', gap: '6px', marginBottom: '14px',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              overflowX: isMobile ? 'auto' : 'unset',
              paddingBottom: isMobile ? '4px' : 0,
              WebkitOverflowScrolling: 'touch',
            }}>
              {serviceFilters.map(f => (
                <Chip
                  key={f.id}
                  label={`${f.icona ? f.icona + ' ' : ''}${f.label}`}
                  active={serviceFilter === f.id}
                  onClick={() => setServiceFilter(f.id)}
                />
              ))}
            </div>

            {/* Filtri data */}
            <div style={{
              display: 'flex', gap: '6px', marginBottom: '14px',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              overflowX: isMobile ? 'auto' : 'unset',
              paddingBottom: isMobile ? '4px' : 0,
              WebkitOverflowScrolling: 'touch',
            }}>
              {[
                { id: 'today', label: '📅 Oggi' },
                { id: 'week', label: '📆 7 giorni' },
                { id: 'month', label: '🗓 30 giorni' },
                { id: 'future', label: '⏭ Future' },
                { id: 'past', label: '⏮ Passate' },
                { id: 'all', label: 'Tutte' },
              ].map(f => (
                <Chip key={f.id} label={f.label} active={dateFilter === f.id} onClick={() => setDateFilter(f.id)} />
              ))}
            </div>

            {/* Contatore */}
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
              {filtered.length} prenotazion{filtered.length === 1 ? 'e' : 'i'}
              {search && ` per "${search}"`}
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size={28} />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
                🏖️ Nessuna prenotazione trovata
              </div>
            ) : (
              <>
                {/* Lista raggruppata per data */}
                {pagedGrouped.map(([date, dateOrders]) => (
                  <div key={date} style={{ marginBottom: '8px' }}>
                    <div style={{
                      fontSize: '11px', color: 'var(--text-muted)',
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                      padding: '6px 0', borderBottom: '1px solid var(--border)',
                      marginBottom: '4px', display: 'flex', justifyContent: 'space-between',
                    }}>
                      <span>{date}</span>
                      <span>{dateOrders.length} prenotazion{dateOrders.length === 1 ? 'e' : 'i'}</span>
                    </div>
                    <BookingList orders={dateOrders} onCancel={cancel} onConfirm={confirm} />
                  </div>
                ))}
                {/* Paginazione */}
                {hasMore && (
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      style={{
                        padding: '8px 24px', borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--border-mid)', background: 'transparent',
                        color: 'var(--text-soft)', fontSize: '13px', cursor: 'pointer',
                        fontFamily: 'var(--font-sans)',
                      }}
                    >
                      Carica altri ({filtered.length - page * PAGE_SIZE} rimanenti)
                    </button>
                  </div>
                )}
              </>
            )}
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

// ─── Chip filtro ────────────────────────────────────────────────────────────

function Chip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 12px', borderRadius: '20px', fontSize: '12px',
      background: active ? 'var(--ocean)' : 'var(--surface)',
      border: `1px solid ${active ? 'var(--ocean)' : 'var(--border)'}`,
      color: active ? '#fff' : 'var(--text-soft)',
      cursor: 'pointer', fontFamily: 'var(--font-sans)', transition: 'all 0.15s',
      flexShrink: 0, whiteSpace: 'nowrap', minHeight: '32px',
    }}>
      {label}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="6" cy="6" r="4.5" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" />
      <line x1="9.5" y1="9.5" x2="13" y2="13" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

// ─── Form nuova prenotazione manuale ────────────────────────────────────────

function NewBookingForm({ beachCode, servizi, onSave, onCancel }) {
  const { isMobile } = useWindowSize();
  const [form, setForm] = useState({
    servizioId: servizi[0]?.id || '',
    guestName: '',
    guestPhone: '',
    notes: '',
    totalPrice: '',
    dateType: 'single',
    dateRange: { dal: today, al: today },
    dates: [today],
    seasonalDays: [],
  });

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%',
  };

  const handleSave = () => {
    const servizio = servizi.find(s => s.id === form.servizioId);
    onSave({
      celle: [], // prenotazione manuale senza posto specifico
      guestName: form.guestName,
      guestPhone: form.guestPhone,
      notes: form.notes,
      totalPrice: Number(form.totalPrice) || 0,
      servizioId: form.servizioId,
      servizioLabel: servizio?.label || '',
      dateType: form.dateType,
      selectedDate: form.dateType === 'single' ? today : undefined,
      dateRange: ['range', 'seasonal'].includes(form.dateType) ? form.dateRange : undefined,
      dates: form.dateType === 'multiple' ? form.dates : undefined,
      seasonalDays: form.dateType === 'seasonal' ? form.seasonalDays : undefined,
    });
  };

  return (
    <div style={{
      background: 'var(--navy-mid)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '20px', marginBottom: '16px',
    }}>
      <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '16px' }}>
        Nuova prenotazione manuale
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
        {/* Servizio */}
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Servizio</label>
          <select value={form.servizioId} onChange={set('servizioId')} style={inputStyle}>
            {servizi.map(s => (
              <option key={s.id} value={s.id}>{s.icona} {s.label}</option>
            ))}
          </select>
        </div>

        {/* Nome cliente */}
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Nome cliente</label>
          <input placeholder="Mario Rossi" value={form.guestName} onChange={set('guestName')} style={inputStyle} />
        </div>

        {/* Telefono */}
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Telefono (opz.)</label>
          <input placeholder="+39 333 1234567" value={form.guestPhone} onChange={set('guestPhone')} style={inputStyle} />
        </div>

        {/* Prezzo */}
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Totale €</label>
          <input type="number" min="0" step="0.5" value={form.totalPrice} onChange={set('totalPrice')} style={inputStyle} />
        </div>

        {/* Note */}
        <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Note (opz.)</label>
          <input placeholder="Note aggiuntive..." value={form.notes} onChange={set('notes')} style={inputStyle} />
        </div>
      </div>

      {/* Tipo date */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Tipo prenotazione
        </label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {DATE_TYPES.map(dt => (
            <button key={dt.id} onClick={() => setForm(p => ({ ...p, dateType: dt.id }))} style={{
              padding: '6px 12px', borderRadius: 'var(--radius-md)', fontSize: '12px',
              border: `1px solid ${form.dateType === dt.id ? 'var(--ocean)' : 'var(--border)'}`,
              background: form.dateType === dt.id ? 'rgba(31,147,224,0.12)' : 'var(--surface)',
              color: form.dateType === dt.id ? 'var(--ocean)' : 'var(--text-soft)',
              cursor: 'pointer', fontFamily: 'var(--font-sans)',
            }}>
              {dt.icon} {dt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Selezione date */}
      {form.dateType === 'single' && (
        <div style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
          Data: <strong style={{ color: 'var(--text-soft)' }}>{today}</strong>
        </div>
      )}

      {(form.dateType === 'range' || form.dateType === 'seasonal') && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          {[['Dal', 'dal'], ['Al', 'al']].map(([lbl, key]) => (
            <div key={key}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{lbl}</label>
              <input type="date" value={form.dateRange[key]}
                onChange={e => setForm(p => ({ ...p, dateRange: { ...p.dateRange, [key]: e.target.value } }))}
                style={inputStyle} />
            </div>
          ))}
        </div>
      )}

      {form.dateType === 'seasonal' && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>Giorni della settimana</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {WEEKDAYS_FULL.map((wd, i) => {
              const active = form.seasonalDays.includes(i);
              return (
                <button key={i} onClick={() => setForm(p => ({
                  ...p, seasonalDays: active ? p.seasonalDays.filter(d => d !== i) : [...p.seasonalDays, i],
                }))} style={{
                  padding: '4px 10px', borderRadius: '20px', fontSize: '11.5px',
                  border: `1px solid ${active ? 'var(--ocean)' : 'var(--border)'}`,
                  background: active ? 'rgba(31,147,224,0.15)' : 'transparent',
                  color: active ? 'var(--ocean)' : 'var(--text-muted)',
                  cursor: 'pointer', fontFamily: 'var(--font-sans)',
                }}>{wd}</button>
              );
            })}
          </div>
        </div>
      )}

      {form.dateType === 'multiple' && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Aggiungi date</label>
          <input type="date" onChange={e => {
            const d = e.target.value;
            if (d && !form.dates.includes(d)) setForm(p => ({ ...p, dates: [...p.dates, d].sort() }));
            e.target.value = '';
          }} style={inputStyle} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '8px' }}>
            {form.dates.map(d => (
              <div key={d} style={{
                padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px',
                background: 'rgba(31,147,224,0.15)', border: '1px solid rgba(31,147,224,0.3)',
                color: 'var(--ocean)', display: 'flex', alignItems: 'center', gap: '5px',
              }}>
                {d}
                <span onClick={() => setForm(p => ({ ...p, dates: p.dates.filter(x => x !== d) }))}
                  style={{ cursor: 'pointer', fontSize: '13px' }}>×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        <Button variant="primary" onClick={handleSave} disabled={!form.servizioId}>
          Salva prenotazione
        </Button>
        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </div>
  );
}